import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { IConstraint } from '../types/brandMenu';
import { getAllCartItemNames } from '../utils/cart';
import { getRestaurantGroups } from '../utils/menuUnification.utils';
import {
  buildSlotBoundUpsellMap,
  resolveSlotBoundOffer,
  SlotBoundHostGroup,
} from '../utils/slotBoundUpsell';
import { selectIsSlotBoundUpsellEnabled } from '../redux/features/config/config.selector';
import {
  selectModGroupPrompt,
  selectOfferedUpsells,
  selectWithheldConstraints,
} from '../redux/features/experiments/experiments.selector';
import { IPromptSlot } from '../redux/features/experiments/experiments.props';
import { isConstraintWithheld } from '../redux/features/experiments/experiments.utils';
import { selectCartItems, selectModGroupsWithDescriptions } from './cart';
import {
  selectBrandMenu,
  selectBrandMenuConstraints,
  selectIsExpressUpsellSuppressionActive,
  selectRestaurantMealPeriodsByName,
} from './menu';
import {
  selectActiveRestaurantCode,
  selectActiveRestaurantTimezone,
  selectPrimaryRestaurantCode,
} from './restaurant';

// HITL-1761: `{ slot: upsells bound to it }`, rebuilt only when the menu changes.
export const selectSlotBoundUpsellsBySlot = createSelector(
  selectBrandMenuConstraints,
  buildSlotBoundUpsellMap
);

// Brand-menu groups are keyed by the primary code — in mirroring the selected
// code differs from the one in restaurant_groups.
const selectCurrentRestaurantGroups = createSelector(
  selectPrimaryRestaurantCode,
  selectActiveRestaurantCode,
  selectBrandMenu,
  (primaryCode, activeCode, brandMenu) =>
    getRestaurantGroups(
      primaryCode || activeCode || '',
      brandMenu?.restaurant_groups || []
    )
);

const selectSlotBoundOfferInputs = createSelector(
  selectIsSlotBoundUpsellEnabled,
  selectSlotBoundUpsellsBySlot,
  selectBrandMenuConstraints,
  selectCartItems,
  selectWithheldConstraints,
  selectOfferedUpsells,
  selectIsExpressUpsellSuppressionActive,
  selectActiveRestaurantTimezone,
  selectRestaurantMealPeriodsByName,
  selectCurrentRestaurantGroups,
  (
    enabled,
    bySlot,
    constraints,
    cartItems,
    withheldConstraints,
    offeredUpsells,
    isExpressSuppressionActive,
    timezone,
    restaurantMealPeriodsByName,
    currentRestaurantGroups
  ) => ({
    enabled,
    bySlot,
    constraints,
    cartItems,
    withheldConstraints,
    offeredUpsells,
    isExpressSuppressionActive,
    timezone,
    restaurantMealPeriodsByName,
    currentRestaurantGroups,
  })
);

type SlotBoundOfferInputs = ReturnType<typeof selectSlotBoundOfferInputs>;

const resolveFromInputs = (
  inputs: SlotBoundOfferInputs,
  modGroup: SlotBoundHostGroup,
  cartItemName?: string
): IConstraint | null => {
  // Nothing to resolve with the flag off or no bound slot: skip the cart walk.
  if (!inputs.enabled || !inputs.bySlot[modGroup.name]) return null;
  return resolveSlotBoundOffer(modGroup, {
    ...inputs,
    ancestorNames: cartItemName ? [cartItemName.toLowerCase()] : [],
    cartItemNames: getAllCartItemNames(inputs.cartItems),
  });
};

/**
 * HITL-1761: the bound upsell to speak when the agent asks `modGroup` on the
 * cart item named `cartItemName`, or null to ask the slot's own question.
 */
export const selectSlotBoundOffer = (
  state: RootState,
  modGroup: SlotBoundHostGroup,
  cartItemName?: string
): IConstraint | null =>
  resolveFromInputs(selectSlotBoundOfferInputs(state), modGroup, cartItemName);

/**
 * What the agent's ask of `modGroup` speaks: the bound upsell's prompt while
 * that offer stands, otherwise the slot's own (undefined while the car
 * withholds it). Read by the info button so its tooltip shows the offer the
 * click will speak.
 */
export const selectModGroupSpokenPrompt = (
  state: RootState,
  slot: IPromptSlot,
  modGroup: SlotBoundHostGroup & { description?: string | null },
  cartItemName?: string
): string | undefined => {
  // A slot the car withholds speaks nothing — the click is suppressed before
  // any offer is resolved (`sendModGroupTTSMessage`) — so the tooltip must
  // not promise the offer either.
  if (isConstraintWithheld(selectWithheldConstraints(state), modGroup.name))
    return undefined;
  return (
    selectSlotBoundOffer(state, modGroup, cartItemName)?.prompt ??
    selectModGroupPrompt(state, slot, modGroup.description)
  );
};

/**
 * The omnibar's auto-prompt list with the slot-bound offer applied to the
 * prompt it asks next (index 0, the only one it speaks). `slotBoundOffer` is
 * set when that prompt is the offer. Memoised on its inputs, so the 2pm window
 * is read when the cart, menu or session changes — the same moment the next
 * prompt itself changes.
 */
export const selectAutoPromptWithSlotBoundOffer = createSelector(
  selectModGroupsWithDescriptions,
  selectSlotBoundOfferInputs,
  (autoPrompt, inputs) => {
    const [modGroup] = autoPrompt.allModGroups;
    const [slot] = autoPrompt.allSlots;
    const cartItemName =
      slot?.cartItemId !== undefined
        ? inputs.cartItems[slot.cartItemId]?.name
        : undefined;
    const slotBoundOffer = modGroup
      ? resolveFromInputs(inputs, modGroup, cartItemName)
      : null;
    if (!slotBoundOffer) return { ...autoPrompt, slotBoundOffer: null };
    return {
      ...autoPrompt,
      allDescriptions: [
        slotBoundOffer.prompt,
        ...autoPrompt.allDescriptions.slice(1),
      ],
      slotBoundOffer,
    };
  }
);
